import {
  Ev2ExperimentLinks,
  Ev2ExperimentStage,
  PipelineStatus,
  PipelineStatusColor,
} from "../Types";
import {
  CLONE_KEY,
  CREATE_KEY,
  EXP_STUDIO_PATH,
  EXP_STUDIO_REPLACE_KEY,
  EXPERIMENT_PATH_KEY,
  EXTENSIONS_PATH_KEY,
} from "./shared.consts";
import { Icon, IColumn } from "@fluentui/react";
import { styles } from "./shared.styles";
import {
  HEALTH_CHECK_ACTION,
  MPAC_TYPE,
  PROD_TYPE,
  RC_TYPE,
  ROLLBACK_TYPE,
} from "../Experiment/RolloutDetails/RolloutDetailsTable/RolloutDetailsEv2Panel/ev2TreatmentStages.consts";

export interface ColumnSortState {
  sortColumn?: string;
  isSortDescending: boolean;
}

/**
 * Returns the path for experiments based on the extension name, experiment name, and flags.
 * @param extensionName name of the extension, this is required
 * @param experiment name of the experiment, optional
 * @param isClone whether the path is for cloning an experiment, optional
 * @param isCreate whether the path is for creating a new experiment, optional
 */
export const getExperimentsPath = (
  extensionName: string,
  experiment?: string,
  { isClone, isCreate }: { isClone?: boolean; isCreate?: boolean } = {},
) => {
  if (!experiment) {
    if (isCreate) {
      return `/${EXTENSIONS_PATH_KEY}/${extensionName}/${EXPERIMENT_PATH_KEY}/${CREATE_KEY}`;
    }
    return `/${EXTENSIONS_PATH_KEY}/${extensionName}`;
  }
  if (isClone) {
    return `/${EXTENSIONS_PATH_KEY}/${extensionName}/${EXPERIMENT_PATH_KEY}/${experiment}/${CLONE_KEY}`;
  }
  return `/${EXTENSIONS_PATH_KEY}/${extensionName}/${EXPERIMENT_PATH_KEY}/${experiment}`;
};

/**
 * Returns the root path for EV2 experiments based on the extension name.
 * @param extensionName name of the extension
 */
export const getRootEv2ExperimentsPath = (extensionName: string) => {
  return `/${EXTENSIONS_PATH_KEY}/${extensionName}`;
};

/**
 * Returns the expstudio path for a given extension and optional experiment
 * @param extensionName name of the extension
 * @param experiment name of the experiment, optional
 */
export const getExpStudioPath = (
  extensionName: string,
  experiment?: string,
) => {
  if (experiment) {
    return EXP_STUDIO_PATH.replace(
      EXP_STUDIO_REPLACE_KEY,
      `${extensionName}/${experiment}`,
    );
  }
  return EXP_STUDIO_PATH.replace(EXP_STUDIO_REPLACE_KEY, `${extensionName}`);
};

/**
 * Normalizes EV2 experiment stage status (e.g., "succeeded" becomes "completed"). Handles converting HEALTH_CHECK_ACTION failures to WARNING.
 * @param result the status that will be normalized
 */
export const normalizeEv2Status = (
  result: Ev2ExperimentStage,
): PipelineStatus => {
  const status = result.status;
  const initialStatus = normalizeStatus(status);
  if (
    initialStatus === PipelineStatus.FAILED &&
    result.rolloutAction === HEALTH_CHECK_ACTION
  ) {
    return PipelineStatus.WARNING;
  }
  return initialStatus;
};

/**
 * Normalizes status names for consistent display (e.g., "succeeded" becomes "completed").
 * @param result the status string to normalize
 */
export const normalizeStatus = (result: string | null): PipelineStatus => {
  if (!result) return PipelineStatus.UNKNOWN;

  switch (result.toLowerCase()) {
    case PipelineStatus.COMPLETED:
    case PipelineStatus.SUCCEEDED:
      return PipelineStatus.COMPLETED;
    case PipelineStatus.FAILED:
      return PipelineStatus.FAILED;
    case PipelineStatus.CANCELED:
    case PipelineStatus.CANCELLED:
      return PipelineStatus.CANCELED;
    case PipelineStatus.IN_PROGRESS:
    case PipelineStatus.RUNNING:
      return PipelineStatus.RUNNING;
    case PipelineStatus.PENDING:
    case PipelineStatus.WAITING:
      return PipelineStatus.PENDING;
    case PipelineStatus.NOT_AVAILABLE:
      return PipelineStatus.NOT_AVAILABLE;
    default:
      return PipelineStatus.UNKNOWN;
  }
};

/**
 * Returns the color associated with a given pipeline status.
 * @param status the pipeline status or status string
 */
export const getStatusColor = (status: PipelineStatus | string): string => {
  switch (status?.toLowerCase()) {
    case PipelineStatus.COMPLETED:
    case PipelineStatus.SUCCEEDED:
      return PipelineStatusColor.COMPLETED;
    case PipelineStatus.WARNING:
      return PipelineStatusColor.WARNING;
    case PipelineStatus.FAILED:
      return PipelineStatusColor.FAILED;
    case PipelineStatus.CANCELED:
    case PipelineStatus.CANCELLED:
      return PipelineStatusColor.CANCELED;
    case PipelineStatus.CANCELLING:
      return PipelineStatusColor.PENDING;
    case PipelineStatus.IN_PROGRESS:
    case PipelineStatus.RUNNING:
      return PipelineStatusColor.RUNNING;
    case PipelineStatus.PENDING:
    case PipelineStatus.WAITING:
      return PipelineStatusColor.PENDING;
    case PipelineStatus.NOT_AVAILABLE:
      return PipelineStatusColor.NOT_AVAILABLE;
    default:
      return PipelineStatusColor.UNKNOWN;
  }
};

/**
 * Returns the icon associated with a given pipeline status.
 * @param status the pipeline status or status string
 */
export const getStatusIcon = (
  status: PipelineStatus | string,
): React.ReactNode => {
  switch (status?.toLocaleLowerCase()) {
    case "completed":
      return (
        <Icon
          iconName="CompletedSolid"
          className={styles.statusIcon}
          data-testid="status-icon"
        />
      );
    case "running":
      return (
        <Icon
          iconName="SyncStatusSolid"
          className={styles.statusIconSpinning}
          data-testid="status-icon"
        />
      );
    case "failed":
      return (
        <Icon
          iconName="StatusErrorFull"
          className={styles.statusIcon}
          data-testid="status-icon"
        />
      );
    case "warning":
      return (
        <Icon
          iconName="AlertSolid"
          className={styles.statusIcon}
          data-testid="status-icon"
        />
      );
    case "canceled":
      return (
        <Icon
          iconName="SkypeCircleSlash"
          className={styles.statusIcon}
          data-testid="status-icon"
        />
      );
    case "pending":
      return (
        <Icon
          iconName="CircleFill"
          className={styles.statusIcon}
          data-testid="status-icon"
        />
      );
    case "skipped":
      return (
        <Icon
          iconName="PageRight"
          className={styles.statusIconPageRight}
          data-testid="status-icon"
        />
      );
    default:
      return (
        <Icon
          iconName="UnknownSolid"
          className={styles.statusIcon}
          data-testid="status-icon"
        />
      );
  }
};

/**
 * Parses the rollout ID from an EV2 experiment link
 * @param ev2Link the EV2 experiment link string whose rollout ID is to be extracted
 */
export function getRolloutId(ev2Link: string | null): string {
  if (!ev2Link) return "";
  const splitLink = ev2Link.split("/");
  return splitLink[splitLink.length - 1];
}

/**
 * Parses the treatment (1%, 10%, Full Rollback, etc...) from an EV2 experiment stage name
 * @param stageName the name of the EV2 experiment stage
 * @param includePercent if true, appends "%" to the treatment number
 * @param abnVariantCount the number of variants in an A/B/n experiment, used to calculate accurate treatment percentages for experiments with more than 2 variants
 */
export function getExpTreatment(
  stageName: string,
  includePercent = false,
  abnVariantCount?: number,
): string {
  let treatmentMatch = null;
  if (stageName.includes(ROLLBACK_TYPE)) {
    return ROLLBACK_TYPE;
  }

  if (!stageName.includes("Treatment")) {
    // A/B/n experiment
    treatmentMatch = stageName.match(/(\d+).Stamp\(\d+\)/i);
  } else {
    // A/B experiment
    treatmentMatch = stageName.match(/Treatment\s+(\d+)/i);
  }

  let treatment = treatmentMatch ? treatmentMatch[1] : "";
  if (treatment && abnVariantCount && abnVariantCount > 2) {
    const treatmentPercent = Number(treatment);
    const treatmentCount = abnVariantCount - 1;
    // Attempting to match how exp studio calculates treatment percentages
    // which first gets the floor of treatment%/variant count
    // This doesn't end up adding up to the stage% but it matches what is shown exp studio
    const floorTreatmentPercent =
      Math.floor((treatmentPercent / abnVariantCount) * 10) / 10;
    treatment = `${Math.round(treatmentCount * floorTreatmentPercent * 10) / 10}`;
  }
  if (includePercent && treatment) {
    return `${treatment}%`;
  }
  return treatment;
}

/**
 * Parses the environment from an EV2 experiment (RC, MPAC, PROD) stage name
 * @param stageName the name of the EV2 experiment stage
 */
export function getExpEnvironment(stageName: string) {
  if (stageName.toUpperCase().includes(RC_TYPE)) {
    return RC_TYPE;
  }
  if (stageName.toUpperCase().includes(MPAC_TYPE)) {
    return MPAC_TYPE;
  }
  if (stageName.toUpperCase().includes(PROD_TYPE)) {
    return PROD_TYPE;
  }

  return "Unknown";
}

/**
 * Retrieves the experiment links from an EV2 experiment stage
 * @param rollout ev2 rollout whose links are to be retrieved
 */
export function getExpLinks(rollout: Ev2ExperimentStage): Ev2ExperimentLinks {
  return rollout.links;
}

/**
 * Order of statuses for sorting purposes
 */
export const StatusOrder = [
  PipelineStatus.RUNNING,
  PipelineStatus.FAILED,
  PipelineStatus.PENDING,
  PipelineStatus.CANCELED,
  PipelineStatus.COMPLETED,
  PipelineStatus.UNKNOWN,
];

/**
 *  Will strip all special characters from a string, leaving only alphanumeric characters.
 * @param value string to strip
 */
export const stripAllSpecialChars = (
  value: string | null | undefined,
): string => {
  const stringValue = value ? value.replace(/[^a-zA-Z0-9]/g, "") : "";
  return stringValue.toLocaleLowerCase();
};

/**
 * Determines the readable type ("string", "number", or "boolean") from a string value
 * @param value the string value to evaluate
 */
export const getReadableTypeFromString = (
  value: string,
): "string" | "number" | "boolean" => {
  const lower = value.trim().toLowerCase();
  if (lower === "true" || lower === "false") return "boolean";
  if (value.trim() !== "" && !isNaN(Number(value)) && isFinite(Number(value)))
    return "number";
  return "string";
};

/**
 * Converts a string value to its corresponding boolean, number, or string (e.g., "true" becomes true, "42" becomes 42).
 * @param value the string value to convert
 */
export const getValueFromString = (
  value: string,
): string | boolean | number => {
  const lower = value.trim().toLowerCase();
  if (lower === "true" || lower === "false") return lower === "true";
  if (value.trim() !== "" && !isNaN(Number(value)) && isFinite(Number(value)))
    return Number(value);
  return value;
};

/**
 Returns true if the value is an empty array, false otherwise
 * @param value the value to check
 */
export const isEmptyArray = (value: any): boolean => {
  return Array.isArray(value) && value.length === 0;
};

/**
 * Returns true if the value is an empty object (not null, not array, no keys), false otherwise
 * @param value the value to check
 */
export const isEmptyObject = (value: any): boolean => {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  );
};

/**
 * Creates a column click handler for sorting
 * @param setSortColumn function to set the current sort column
 * @param setIsSortDescending function to set the sort direction
 * @param currentSortColumn the current sort column
 * @param currentIsSortDescending the current sort direction

 */
export const createColumnClickHandler = (
  setSortColumn: (column: string | undefined) => void,
  setIsSortDescending: (descending: boolean) => void,
  currentSortColumn?: string,
  currentIsSortDescending = false,
) => {
  return (ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
    const newIsSortDescending =
      column.key === currentSortColumn ? !currentIsSortDescending : false;
    setSortColumn(column.key);
    setIsSortDescending(newIsSortDescending);
  };
};

/**
 * Adds sorting properties to a column for UI rendering based off the incoming column key
 * @param column the column to add sorting properties to (based off column.key)
 * @param sortState the current sorting state
 * @param onColumnClick optional click handler for the column
 */
export const addSortPropsToColumn = (
  column: IColumn,
  sortState: ColumnSortState,
  onColumnClick?: (ev: React.MouseEvent<HTMLElement>, column: IColumn) => void,
): IColumn => {
  const { sortColumn, isSortDescending } = sortState;

  return {
    ...column,
    isSorted: sortColumn === column.key,
    isSortedDescending: sortColumn === column.key ? isSortDescending : false,
    onColumnClick,
  };
};

/**
 * Debug logging functions that only output in development environment
 */

/**
 * Debug logging that only shows in development
 */
export function debugLog(message: string, ...args: any[]) {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.log(`[DEBUG] ${message}`, ...args);
  }
}

/**
 * Warning logging that only shows in development
 */
export function debugWarn(message: string, ...args: any[]) {
  if (process.env.NODE_ENV === "development") {
    console.warn(`[DEBUG] ${message}`, ...args);
  }
}

/**
 * Error logging that only shows in development
 */
export function debugError(message: string, ...args: any[]) {
  if (process.env.NODE_ENV === "development") {
    console.error(`[DEBUG] ${message}`, ...args);
  }
}
